import { Button, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import CheckboxValueGroup from '../../components/CheckboxValueGroup/CheckboxValueGroup';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import { GridRenderer, GridRendererProps } from '../../components/Grid/Grid';
import { useStore } from '../../hooks/use-store';
import { RoleApi } from '../../types/users-types';

const PHONE_LENGTH = 10;

export interface UserForEditing {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  roles: RoleApi[];
}

interface RowCellRendererProps extends GridRendererProps<UserForEditing> {
  field: keyof UserForEditing;
}

const RowCellRenderer = observer((props: RowCellRendererProps) => {
  const { row, index, field } = props;
  const store = useStore().practiceUserManagementStore;

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (field === 'firstName' || index === 0) {
      inputRef.current?.focus();
    }
  }, [field, index]);

  const changeValue = (fieldToChange: keyof UserForEditing, value: string): void => {
    if (fieldToChange === 'roles' || (fieldToChange === 'phone' && !/^\d*$/.test(value))) return;
    runInAction(() => {
      store.currentUser[fieldToChange] = value;
    });
  };

  if (store.currentIndex === index && field !== 'roles' && (store.addMode || field !== 'email')) {
    return (
      <FieldGroupInCell
        htmlFor={`cell-text-box-${index}_${field}`}
        errorText={index === store.currentIndex ? store.userError[field] : undefined}
      >
        <TextBox
          ref={inputRef}
          id={`cell-text-box-${index}_${field}`}
          data-testid={`table-cell-text-box_${field}`}
          placeholder={field === 'phone' ? 'Add Phone' : field === 'email' ? 'MyIDEXX email' : ''}
          value={store.currentUser[field] || ''}
          disabled={store.loaders.user}
          maxLength={field === 'phone' ? PHONE_LENGTH : undefined}
          onChange={e => changeValue(field, e.target.value)}
        />
      </FieldGroupInCell>
    );
  }
  if (store.currentIndex === index && field === 'roles') {
    return (
      <CheckboxValueGroup
        id='practice-roles-group'
        elementsClass='item-editors'
        variant='horizontal'
        small
        values={store.roles}
        displayField='description'
        onChange={store.changeRoleIndices.bind(this)}
        checkedIndices={store.currentRoleIndices}
        disabledByIndex={i => store.loaders.user || store.isRoleDisabled(i)}
        data-testid='user-role'
      />
    );
  }
  return (
    <div className='plain-text item-editors' role='table-cell-text' aria-label={`User ${field}`}>
      {field === 'roles'
        ? row.roles
            .filter(r => !r.hidden)
            .map(r => r.description)
            .join(', ')
        : row[field]}
    </div>
  );
});

export const ActionsRenderer = observer((props: GridRendererProps<UserForEditing>) => {
  const { row, index } = props;
  const store = useStore().practiceUserManagementStore;
  const editMode = store.currentIndex !== undefined;
  const disabled = store.loaders.user;
  if (store.currentIndex === index)
    return (
      <>
        <Button
          variant='link'
          iconOnly='save'
          size='small'
          disabled={disabled}
          onClick={() => void store.validateAndUpdateUser()}
          title='Save changes'
          aria-label='Save Changes'
          data-testid={`save-${row.email}-btn`}
        />
        <Button
          variant='link'
          iconOnly='cancel'
          size='small'
          disabled={disabled}
          onClick={() => store.cancelEditingUser()}
          title='Cancel changes'
          aria-label='Cancel Changes'
          data-testid={`cancel-${row.email}-btn`}
        />
      </>
    );
  return (
    <>
      <Button
        variant='link'
        iconOnly='edit'
        size='small'
        disabled={disabled || editMode}
        onClick={() => store.editUser(index)}
        title='Edit user'
        aria-label='Edit User'
        data-testid={`edit-${row.email}-btn`}
      />
      <Button
        variant='link'
        iconOnly='delete'
        size='small'
        disabled={disabled || editMode}
        onClick={() => store.deleteUser(index)}
        title='Delete user'
        aria-label='Delete User'
        data-testid={`delete-${row.email}-btn`}
      />
    </>
  );
});

export const rowCellRenderer =
  (field: keyof UserForEditing): GridRenderer<UserForEditing> =>
  (props: GridRendererProps<UserForEditing>) => (
    <RowCellRenderer row={props.row} index={props.index} field={field} />
  );
